// PaymentsManagement: List online/offline, verify/reject, upload proof
import React, { useEffect, useState } from 'react';
import BookingManagement from './BookingManagement';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, Stack, Paper, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentsIcon from '@mui/icons-material/Payments';
import GroupIcon from '@mui/icons-material/Group';
import api from '../../services/api';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import FlagIcon from '@mui/icons-material/Flag'; // If you use MUI icons, or use a custom flag component

function PaymentsManagement() {
        // Helper: get unique user count from paidBookers
        const getUniqueUserCount = (arr, status) => {
          const filtered = status ? arr.filter(p => p.status === status) : arr;
          const userIds = new Set(filtered.map(p => p.bookingId?.userId?._id || p.bookingId?.userId || p.userId?._id || p.userId || p.bookingId || p._id));
          return userIds.size;
        };

        // Helper: get unique user IDs for a given status
        const getUniqueUserIds = (arr, status) => {
          const filtered = status ? arr.filter(p => p.status === status) : arr;
          return Array.from(new Set(filtered.map(p => p.bookingId?.userId?._id || p.bookingId?.userId || p.userId?._id || p.userId || p.bookingId || p._id)));
        };

        // Helper: get average per person
        const getAvgPerPerson = (arr) => {
          const userMap = {};
          arr.forEach(p => {
            const userId = p.bookingId?.userId?._id || p.bookingId?.userId || p.userId?._id || p.userId || p.bookingId || p._id;
            if (!userMap[userId]) userMap[userId] = 0;
            userMap[userId] += Number(p.amount) || 0;
          });
          const vals = Object.values(userMap);
          if (!vals.length) return 0;
          return vals.reduce((a, b) => a + b, 0) / vals.length;
        };
      const theme = typeof useTheme === 'function' ? useTheme() : (window && window.theme ? window.theme : { palette: { mode: 'light', background: { paper: '#fff' }, text: { primary: '#222' } } });
      const isDark = theme.palette?.mode === 'dark';

      // Reusable DataCard component
      const DataCard = ({ icon, label, value, subvalue, color, bg, border }) => (
        <Paper elevation={isDark ? 8 : 2} sx={{
          minWidth: 140,
          maxWidth: 180,
          borderRadius: 3,
          p: 1.2,
          background: bg,
          border: border,
          color: color,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          boxShadow: isDark ? '0 4px 16px #000a' : '0 1px 6px #bcd',
          mb: 1,
        }}>
          <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mb: 0.5 }}>
            {icon && React.cloneElement(icon, { fontSize: 'medium' })}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 12 }}>{label}</Typography>
          </Stack>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.2, lineHeight: 1.1, fontSize: 20 }}>{value}</Typography>
          {subvalue && <Typography variant="caption" sx={{ color: isDark ? theme.palette.text.secondary : 'text.secondary', fontWeight: 500, fontSize: 11 }}>{subvalue}</Typography>}
        </Paper>
      );
    // Confirmation dialog state
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  if (currentUser.role !== 'superadmin' && (!currentUser.paymentRole || currentUser.paymentRole === 'none')) {
    return (
      <SuperAdminLayout>
        <Box sx={{ p: 4 }}>
          <Typography variant="h6">Insufficient payment permissions.</Typography>
        </Box>
      </SuperAdminLayout>
    );
  }
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [proofDialog, setProofDialog] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [unpaidBookers, setUnpaidBookers] = useState([]);
  const [paidBookers, setPaidBookers] = useState([]);
  const [selectedBooker, setSelectedBooker] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/payments'),
      api.get('/api/bookings/unpaid-bookers')
    ])
      .then(([paymentsRes, unpaidRes]) => {
        // paymentsRes.data should be array of payments with booking info populated
        setPaidBookers(paymentsRes.data);
        setUnpaidBookers(unpaidRes.data);
      })
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch payments/bookers', severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  // Show confirmation dialog before delete
  const handleDeleteRequest = (id) => {
    setDeleteTarget(id);
    setConfirmDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await api.delete(`/api/payments/${deleteTarget}`);
      setPayments(prev => prev.filter(p => p._id !== deleteTarget));
      setSnackbar({ open: true, message: 'Payment deleted successfully', severity: 'success' });
      // Refresh paid/unpaid bookers
      const [paidRes, unpaidRes] = await Promise.all([
        api.get('/api/bookings/paid-bookers'),
        api.get('/api/bookings/unpaid-bookers')
      ]);
      setPaidBookers(paidRes.data);
      setUnpaidBookers(unpaidRes.data);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
    } finally {
      setLoading(false);
      setConfirmDelete(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDelete(false);
    setDeleteTarget(null);
  };

  const handleVerify = async (id) => {
    setLoading(true);
    try {
      await api.post(`/api/payments/${id}/verify`);
      setPayments(prev => prev.map(p => p._id === id ? { ...p, status: 'verified' } : p));
      setSnackbar({ open: true, message: 'Payment verified', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Verify failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (id) => {
    setLoading(true);
    try {
      await api.post(`/api/payments/${id}/reject`);
      setPayments(prev => prev.map(p => p._id === id ? { ...p, status: 'rejected' } : p));
      setSnackbar({ open: true, message: 'Payment rejected', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Reject failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleProofOpen = (payment) => {
    setSelectedPayment(payment);
    setProofDialog(true);
  };
  const handleProofClose = () => {
    setProofDialog(false);
    setProofFile(null);
    setSelectedPayment(null);
  };
  const handleProofUpload = async () => {
    if (!proofFile || !selectedPayment) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('proof', proofFile);
      await api.post(`/api/payments/${selectedPayment._id}/proof`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSnackbar({ open: true, message: 'Proof uploaded', severity: 'success' });
      handleProofClose();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Upload failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch unpaid bookers when opening add payment dialog
  const handleAddOpen = () => {
    setAddDialog(true);
  };

  const handleAddClose = () => {
    setAddDialog(false);
    setSelectedBooker('');
    setPaymentAmount('');
  };

  const handleAddPayment = async () => {
    if (!selectedBooker || !paymentAmount) {
      setSnackbar({ open: true, message: 'Select booker and enter amount', severity: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/bookings/add-payment', {
        bookingId: selectedBooker,
        amount: paymentAmount
      });
      setSnackbar({ open: true, message: 'Payment added', severity: 'success' });
      setAddDialog(false);
      setSelectedBooker('');
      setPaymentAmount('');
      // Refresh paid/unpaid bookers
      const [paidRes, unpaidRes] = await Promise.all([
        api.get('/api/bookings/paid-bookers'),
        api.get('/api/bookings/unpaid-bookers')
      ]);
      setPaidBookers(paidRes.data);
      setUnpaidBookers(unpaidRes.data);
      // Refresh bookings in BookingManagement page
      try { window.dispatchEvent(new Event('bookings:refresh')); } catch (e) {}
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Add payment failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ mb: 4, px: { xs: 1, md: 0 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Payments Management
        </Typography>
        <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={handleAddOpen}>Add Payment</Button>
        {/* Adaptive KPI Cards for Payments */}
        <Stack direction="row" spacing={2.5} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <DataCard
            icon={<PaymentsIcon fontSize="large" color="primary" />}
            label="Total Payments (USD)"
            value={`$${paidBookers.reduce((sum, p) => sum + (Number(p.amount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            subvalue={`Paid: ${getUniqueUserCount(paidBookers)} (unique) | ${paidBookers.length} (total) | Unpaid: ${unpaidBookers.length}`}
            color={isDark ? '#007bff' : '#14532d'}
            bg={isDark ? 'linear-gradient(135deg, #14532d 60%, #166534 100%)' : 'linear-gradient(135deg, #eaf2fb 60%, #dbeafe 100%)'}
            border={isDark ? '2px solid #4ade80' : '2px solid #007bff'}
          />
          <DataCard
            icon={<CheckCircleIcon fontSize="large" color="success" />}
            label="Verified Payments"
            value={`${getUniqueUserCount(paidBookers, 'verified')}`}
            subvalue={`${paidBookers.filter(p => p.status === 'verified').length} total`}
            color={isDark ? '#22c55e' : '#166534'}
            bg={isDark ? 'linear-gradient(135deg, #166534 60%, #22c55e 100%)' : 'linear-gradient(135deg, #e8fbe8 60%, #bbf7d0 100%)'}
            border={isDark ? '2px solid #bbf7d0' : '2px solid #22c55e'}
          />
          <DataCard
            icon={<CancelIcon fontSize="large" color="error" />}
            label="Rejected Payments"
            value={`${getUniqueUserCount(paidBookers, 'rejected')}`}
            subvalue={`${paidBookers.filter(p => p.status === 'rejected').length} total`}
            color={isDark ? '#ef4444' : '#7f1d1d'}
            bg={isDark ? 'linear-gradient(135deg, #7f1d1d 60%, #dc2626 100%)' : 'linear-gradient(135deg, #ffeaea 60%, #fecaca 100%)'}
            border={isDark ? '2px solid #fecaca' : '2px solid #ef4444'}
          />
          <DataCard
            icon={<GroupIcon fontSize="large" color="info" />}
            label="Avg. Payment (USD)"
            value={paidBookers.length ? `$${getAvgPerPerson(paidBookers).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '0.00'}
            subvalue={paidBookers.length ? `per person | $${(
              paidBookers.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) / paidBookers.length
            ).toLocaleString('en-US', { minimumFractionDigits: 2 })} per total` : ''}
            color={isDark ? '#6366f1' : '#312e81'}
            bg={isDark ? 'linear-gradient(135deg, #312e81 60%, #6366f1 100%)' : 'linear-gradient(135deg, #f5faff 60%, #c7d2fe 100%)'}
            border={isDark ? '2px solid #c7d2fe' : '2px solid #6366f1'}
          />
        </Stack>
        <NotificationSnackbar
          open={snackbar.open}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          message={snackbar.message}
          severity={snackbar.severity}
        />
        {loading ? <Typography>Loading...</Typography> : (
          <>
            <Typography variant="h6" sx={{ mt: 2 }}>Paid Bookers</Typography>
            <Table sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden', mb: 4 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.paper' }}>
                  <TableCell>Payment ID</TableCell>
                  <TableCell>Booking ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paidBookers.map(p => (
                  <TableRow key={p._id}>
                    <TableCell>{p._id}</TableCell>
                    <TableCell>{p.bookingId?._id || p.bookingId || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 0, minWidth: 120 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'primary.main', mb: 0.2 }}>
                          {p.bookingId?.userId?.name || p.bookingId?.name || '-'}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.2 }}>
                          {p.bookingId?.userId?.email || p.bookingId?.email || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{p.bookingId?.userId?.countryCode ? <img src={`https://flagcdn.com/24x18/${p.bookingId.userId.countryCode.toLowerCase()}.png`} alt={p.bookingId.userId.countryCode} style={{ width: 24, height: 18, borderRadius: 2 }} /> : '-'}</TableCell>
                    <TableCell>{p.amount}</TableCell>
                    <TableCell>{p.status}</TableCell>
                    {currentUser.role === 'superadmin' && (
                      <TableCell>
                        <Button color="error" size="small" variant="outlined" onClick={() => handleDeleteRequest(p._id)}>Delete</Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="h6" sx={{ mt: 2 }}>Unpaid Bookers</Typography>
            <Table sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.paper' }}>
                  <TableCell>Booking ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Amount Due</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unpaidBookers.map(b => (
                  <TableRow key={b.bookingId}>
                    <TableCell>{b.bookingId}</TableCell>
                    <TableCell>{b.name}</TableCell>
                    <TableCell>{b.email}</TableCell>
                    <TableCell>{b.countryCode ? <img src={`https://flagcdn.com/24x18/${b.countryCode.toLowerCase()}.png`} alt={b.countryCode} style={{ width: 24, height: 18, borderRadius: 2 }} /> : '-'}</TableCell>
                    <TableCell>{b.amountDue}</TableCell>
                    <TableCell>{b.status}</TableCell>
                    {currentUser.role === 'superadmin' && b._id && (
                      <TableCell>
                        <Button color="error" size="small" variant="outlined" onClick={() => handleDeleteRequest(b._id)}>Delete</Button>
                      </TableCell>
                    )}
                        {/* Confirm Delete Dialog */}
                        <Dialog open={confirmDelete} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
                          <DialogTitle>Confirm Delete</DialogTitle>
                          <DialogContent>
                            <Typography>Are you sure you want to delete this payment? This action cannot be undone.</Typography>
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={handleDeleteCancel}>Cancel</Button>
                            <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
                          </DialogActions>
                        </Dialog>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {/* Add Payment Dialog */}
        <Dialog open={addDialog} onClose={handleAddClose} maxWidth="xs" fullWidth>
          <DialogTitle>Add Payment</DialogTitle>
          <DialogContent>
            <Select
              fullWidth
              value={selectedBooker}
              onChange={e => setSelectedBooker(e.target.value)}
              displayEmpty
              sx={{ mb: 2 }}
            >
              <MenuItem value="" disabled>Select unpaid booker</MenuItem>
              {unpaidBookers.map(b => (
                <MenuItem key={b.bookingId} value={b.bookingId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>{b.name}</Typography>
                    <Typography sx={{ ml: 1 }}>{b.email}</Typography>
                    {b.countryCode && (
                      <span>
                        <img
                          src={`https://flagcdn.com/24x18/${b.countryCode.toLowerCase()}.png`}
                          alt={b.countryCode}
                          style={{ width: 24, height: 18, borderRadius: 2, marginRight: 4 }}
                        />
                      </span>
                    )}
                    <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                      ({b.bookingId})
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'primary.main', ml: 1 }}>
                      {b.amountDue || 'Amount'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <TextField
              label="Payment Amount"
              type="number"
              fullWidth
              value={paymentAmount}
              onChange={e => setPaymentAmount(e.target.value)}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddClose}>Cancel</Button>
            <Button onClick={handleAddPayment} variant="contained" disabled={!selectedBooker || !paymentAmount}>Add</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={proofDialog} onClose={handleProofClose} maxWidth="xs" fullWidth>
          <DialogTitle>Upload Payment Proof</DialogTitle>
          <DialogContent>
            <input type="file" accept="image/*,application/pdf" onChange={e => setProofFile(e.target.files[0])} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleProofClose}>Cancel</Button>
            <Button onClick={handleProofUpload} variant="contained" disabled={!proofFile}>Upload</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
}

export default PaymentsManagement;
